import { ProductDetails } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductDetails";
import {
  ProductList as SaleorProductList,
  ProductListVariables as SaleorProductListVariables,
} from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductList";
import gql from "graphql-tag";

import { channelSlug, productsPerPage } from "@app/constants";
import { productList } from "@app/views/queries";
import {
  basicProductFragment,
  productPricingFragment,
  shippingMethodsFragment,
} from "@graphql/fragments";
import { lenderProductsQuery, useTypedQuery } from "@graphql/queries";

const similarProductList = gql`
  ${basicProductFragment}
  ${productPricingFragment}
  ${shippingMethodsFragment}
  query SimilarProductList($similarProductId: ID) {
    products(
      first: 8
      similarProductId: $similarProductId
      # NOTES channel is hardcoded since we are not using the 'channel' DB table
      filter: { stockAvailability: IN_STOCK, channel: "default-channel" }
    ) {
      edges {
        node {
          ...BasicProductFields
          ...ProductPricingFieldSF
          ...ShippingMethods
        }
      }
      totalCount
    }
  }
`;

// const getProductById = gql`
//   query GetProductById($productId: ID) {
//     product(id: $productId) {
//       id
//       slug
//       resource {
//         preparationDays
//       }
//       thumbnail {
//         url
//         alt
//       }
//       thumbnail2x: thumbnail(size: 510) {
//         url
//         alt
//       }
//       retailValue {
//         amount
//         currency
//       }
//     }
//   }
// `;

interface ProductList extends SaleorProductList {}

interface ProductListVariables extends SaleorProductListVariables {
  public: boolean;
  similarProductId: string;
}

export const useSimilarProductsQuery = (
  data: ProductDetails["product"] | null | undefined,
  unavailable: boolean
) => {
  if (!data) {
    return {
      data: {
        products: { edges: [] },
      },
    };
  }

  let variables;

  if (unavailable) {
    const sizeAttribute = data?.attributes?.find(
      (attr) => attr.attribute.name === "Size"
    );
    const size = sizeAttribute?.values?.[0]?.name || "Not Found";
    const styleAttribute = data?.attributes?.find(
      (attr) => attr.attribute.name === "Item Style"
    );
    const productStyles =
      styleAttribute?.values?.map(
        (value) => value?.name?.toLowerCase() || ""
      ) || [];

    variables = {
      first: 4,
      filterIsb: true,
      attributes: {
        size: [size],
        "item-style": productStyles,
      },
      channel: channelSlug,
      categoryId: data?.category?.id,
      similarProductId: data?.id,
    };
  } else {
    variables = {
      first: productsPerPage,
      public: true,
      similarProductId: data?.id,
    };
  }

  return useTypedQuery<ProductList, ProductListVariables>(
    unavailable ? productList : similarProductList,
    {
      variables,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );
};

export const useLenderProductsQuery = (slug) => {
  const variables = {
    channel: "default-channel",
    lenderSlug: slug,
  };

  return useTypedQuery<any>(lenderProductsQuery, {
    variables,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
};

// export const getProductByIdQuery = (id) => {
//   const variables = {
//     public: true,
//     productId: id,
//   };

//   return useTypedQuery(getProductById, {
//     variables,
//     fetchPolicy: "no-cache",
//     nextFetchPolicy: "cache-first",
//   });
// };

export type ProductResourceBookingData = {
  product: {
    id: string;
    resource: {
      id: string;
      preparationDays: number;
      bookings: {
        dates: {
          date: string;
        }[];
      }[];
    };
    purchaseStatus: string;
  };
};

const productResourceBookingQuery = gql`
  query ResourceQuery($slug: String!, $channel: String!, $public: Boolean!) {
    product(slug: $slug, channel: $channel, public: $public) {
      id
      slug
      purchaseStatus
      resource {
        id
        preparationDays
        bookings {
          dates {
            date
          }
        }
      }
    }
  }
`;

export const useProductResourceBooking = (slug: string) => {
  const variables = {
    slug,
    channel: channelSlug,
    public: true,
  };

  return useTypedQuery<ProductResourceBookingData>(
    productResourceBookingQuery,
    {
      variables,
      fetchPolicy: "no-cache",
    }
  );
};
